import { LocaleProvider, useLocale, t } from './context/LocaleContext';
import { useAppVersionCheck } from './hooks/useAppVersionCheck';
import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ConfigProvider, Spin, theme } from 'antd';
import zhCN from 'antd/locale/zh_CN';
import enUS from 'antd/locale/en_US';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import Layout from './components/Layout';
import Login from './pages/Login';
import { findMenuByPath, menuItems } from './menuConfig';
import SalesReport from './pages/reports/SalesReport';

// 工作台
const Dashboard = lazy(() => import('./pages/Dashboard'));

// 订单中心
const OrderList = lazy(() => import('./pages/OrderList'));
const OrderDetail = lazy(() => import('./pages/OrderDetail'));
const OrderCreate = lazy(() => import('./pages/OrderCreate'));
const OrderEdit = lazy(() => import('./pages/OrderEdit'));
const OrderReviewFirst = lazy(() => import('./pages/orders/OrderReviewFirst'));
const OrderReviewSecond = lazy(() => import('./pages/orders/OrderReviewSecond'));

const OrderPush = lazy(() => import('./pages/orders/OrderPush'));
const OrderProducing = lazy(() => import('./pages/orders/OrderProducing'));
const OrderException = lazy(() => import('./pages/orders/OrderException'));
const OrderShipped = lazy(() => import('./pages/orders/OrderShipped'));
const OrderCompleted = lazy(() => import('./pages/orders/OrderCompleted'));
const AfterSalesManage = lazy(() => import('./pages/orders/AfterSalesManage'));
const AfterSalesChat = lazy(() => import('./pages/orders/AfterSalesChat'));

// 团队管理
const TeamManage = lazy(() => import('./pages/teams/TeamManage'));
const TeamMemberManage = lazy(() => import('./pages/teams/TeamMemberManage'));
const TeamOrderManage = lazy(() => import('./pages/teams/TeamOrderManage'));

// 门店管理
const StoreManagement = lazy(() => import('./pages/stores/StoreManagement'));
const StoreList = lazy(() => import('./pages/stores/StoreList'));
const StoreDetail = lazy(() => import('./pages/stores/StoreDetail'));
const StoreAccountManage = lazy(() => import('./pages/stores/StoreAccountManage'));
const ConsumerManage = lazy(() => import('./pages/stores/ConsumerManage'));
const CustomerRegistrationAudit = lazy(() => import('./pages/stores/CustomerRegistrationAudit'));


// 采购管理
const PurchaseApply = lazy(() => import('./pages/purchase/PurchaseApply'));
const FabricInventory = lazy(() => import('./pages/purchase/FabricInventory'));
const FabricImport = lazy(() => import('./pages/purchase/FabricImport'));
const SampleBookConfig = lazy(() => import('./pages/master/SampleBookConfig'));
const OrderInboundOutbound = lazy(() => import('./pages/purchase/OrderInboundOutbound'));

// 仓储物流
const ReceiveManage = lazy(() => import('./pages/warehouse/ReceiveManage'));
const QCManage = lazy(() => import('./pages/warehouse/QCManage'));
const ShipManage = lazy(() => import('./pages/warehouse/ShipManage'));


// 成衣商城
const ReadyWearCreate = lazy(() => import('./pages/master/ReadyWearCreate'));

// 基础数据

const PriceSystemConfig = lazy(() => import('./pages/master/PriceSystemConfig'));
const FrontCategory = lazy(() => import('./pages/master/FrontCategory'));
const CommissionConfig = lazy(() => import('./pages/master/CommissionConfig'));
const PenaltyConfig = lazy(() => import('./pages/master/PenaltyConfig'));
const ReadyWearManage = lazy(() => import('./pages/master/ReadyWearManage'));
const BaseOrderConfig = lazy(() => import('./pages/master/BaseOrderConfig'));
const EaseRuleConfig = lazy(() => import('./pages/master/EaseRuleConfig'));
const DesignOptionRules = lazy(() => import('./pages/master/DesignOptionRules'));


// 供应商管理
const MaterialSupplier = lazy(() => import('./pages/supplier/MaterialSupplier'));
const FactorySupplier = lazy(() => import('./pages/supplier/FactorySupplier'));
const FactoryMappingManager = lazy(() => import('./pages/supplier/FactoryMappingManager'));
const FactoryMappingImport = lazy(() => import('./pages/supplier/FactoryMappingImport'));

// 财务管理
const CommissionCalculator = lazy(() => import('./pages/finance/CommissionCalculator'));
const MySalary = lazy(() => import('./pages/finance/MySalary'));
const SalaryConfig = lazy(() => import('./pages/finance/SalaryConfig'));
const SalaryCalculator = lazy(() => import('./pages/finance/SalaryCalculator'));
const SalarySummary = lazy(() => import('./pages/finance/SalarySummary'));
const YearEndBonus = lazy(() => import('./pages/finance/YearEndBonus'));
const DiscountApproval = lazy(() => import('./pages/finance/DiscountApproval'));
const QuoteGenerator = lazy(() => import('./pages/finance/QuoteGenerator'));
const QuoteTool = lazy(() => import('./pages/finance/QuoteTool'));
const SalesPriceTable = lazy(() => import('./pages/finance/SalesPriceTable'));
const QuoteView = lazy(() => import('./pages/finance/QuoteView'));
const InvoiceManage = lazy(() => import('./pages/finance/InvoiceManage'));
const FactoryReconciliationList = lazy(() => import('./pages/finance/FactoryReconciliationList'));
const InvoiceDetail = lazy(() => import('./pages/finance/InvoiceDetail'));
const InvoiceGenerator = lazy(() => import('./pages/finance/InvoiceGenerator'));
const InvoiceTool = lazy(() => import('./pages/finance/InvoiceTool'));
const SupplierConfirm = lazy(() => import('./pages/purchase/SupplierConfirm'));
const BatchShareView = lazy(() => import('./pages/purchase/BatchShareView'));

// 系统设置
const UserManage = lazy(() => import('./pages/settings/UserManage'));
const RoleManage = lazy(() => import('./pages/settings/RoleManage'));
const SwitchUser = lazy(() => import('./pages/settings/SwitchUser'));
const SystemConfig = lazy(() => import('./pages/settings/SystemConfig'));
const MessageCenter = lazy(() => import('./pages/settings/MessageCenter'));
const OperationLog = lazy(() => import('./pages/settings/OperationLog'));
const RecycleBin = lazy(() => import('./pages/settings/RecycleBin'));
const HpHistoricalOrders = lazy(() => import('./pages/HpHistoricalOrders'));
const MyPurchase = lazy(() => import('./pages/purchase/MyPurchase'));

// 报表中心
const OrderReport = lazy(() => import('./pages/reports/OrderReport'));
const CostReport = lazy(() => import('./pages/reports/CostReport'));
const ProfitReport = lazy(() => import('./pages/reports/ProfitReport'));

const PageLoader: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense fallback={
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      gap: 16
    }}>
      <Spin size="large" />
      <div style={{ color: '#888', fontSize: 13 }}>{t('加载中...')}</div>
    </div>
  }>
    {children}
  </Suspense>
);

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading, hasMenuPermission } = useAuth();
  const location = useLocation();
  if (loading) return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh'
    }}>
      <Spin size="large" description={t('加载中...')} />
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  // 路由权限校验（基于 permissions.menus）
  const menu = findMenuByPath(menuItems, location.pathname);
  if (menu && !hasMenuPermission(menu.key)) {
    // 避免在 / 路径上无限重定向
    if (location.pathname === '/') {
      return (
        <Layout>
          <div style={{ padding: 40, textAlign: 'center' }}>
            <h2>{t('无权限访问工作台')}</h2>
            <p>{t('当前账号没有权限访问首页，请联系管理员')}</p>
          </div>
        </Layout>
      );
    }
    return <Navigate to="/" replace />;
  }
  return <Layout>{children}</Layout>;
};

function AppRoutes() {
  // 版本更新提示：放在 AuthProvider 内部，等登录态就绪后只检查一次
  useAppVersionCheck();
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      {/* 工作台 */}
      <Route path="/" element={<PrivateRoute><PageLoader><Dashboard /></PageLoader></PrivateRoute>} />
      {/* 订单中心 — 注意：静态路由必须放在 /orders/:id 动态路由之前 */}
      <Route path="/orders" element={<PrivateRoute><PageLoader><OrderList /></PageLoader></PrivateRoute>} />
      <Route path="/orders/create" element={<PrivateRoute><PageLoader><OrderCreate /></PageLoader></PrivateRoute>} />
      <Route path="/orders/first-review" element={<PrivateRoute><PageLoader><OrderReviewFirst /></PageLoader></PrivateRoute>} />
      <Route path="/orders/second-review" element={<PrivateRoute><PageLoader><OrderReviewSecond /></PageLoader></PrivateRoute>} />
      <Route path="/orders/push" element={<PrivateRoute><PageLoader><OrderPush /></PageLoader></PrivateRoute>} />
      <Route path="/orders/producing" element={<PrivateRoute><PageLoader><OrderProducing /></PageLoader></PrivateRoute>} />
      <Route path="/orders/exception" element={<PrivateRoute><PageLoader><OrderException /></PageLoader></PrivateRoute>} />
      <Route path="/orders/shipped" element={<PrivateRoute><PageLoader><OrderShipped /></PageLoader></PrivateRoute>} />
      <Route path="/orders/completed" element={<PrivateRoute><PageLoader><OrderCompleted /></PageLoader></PrivateRoute>} />
      <Route path="/orders/after-sales" element={<PrivateRoute><PageLoader><AfterSalesManage /></PageLoader></PrivateRoute>} />
      <Route path="/orders/after-sales/:after_sales_id/chat" element={<PrivateRoute><PageLoader><AfterSalesChat /></PageLoader></PrivateRoute>} />
      <Route path="/orders/hp-history" element={<PrivateRoute><PageLoader><HpHistoricalOrders /></PageLoader></PrivateRoute>} />
      <Route path="/orders/my-purchase" element={<PrivateRoute><PageLoader><MyPurchase /></PageLoader></PrivateRoute>} />
      <Route path="/orders/:id/edit" element={<PrivateRoute><PageLoader><OrderEdit /></PageLoader></PrivateRoute>} />
      <Route path="/orders/:id" element={<PrivateRoute><PageLoader><OrderDetail /></PageLoader></PrivateRoute>} />
      {/* 团队管理 */}
      <Route path="/teams" element={<PrivateRoute><PageLoader><TeamManage /></PageLoader></PrivateRoute>} />
      <Route path="/teams/members" element={<PrivateRoute><PageLoader><TeamMemberManage /></PageLoader></PrivateRoute>} />
      <Route path="/teams/orders" element={<PrivateRoute><PageLoader><TeamOrderManage /></PageLoader></PrivateRoute>} />
      {/* 门店管理 — 注意：静态路由必须放在 /stores/:id 动态路由之前 */}
      <Route path="/store-management" element={<PrivateRoute><PageLoader><StoreManagement /></PageLoader></PrivateRoute>} />
      <Route path="/stores" element={<PrivateRoute><PageLoader><StoreList /></PageLoader></PrivateRoute>} />
      <Route path="/stores/accounts" element={<PrivateRoute><PageLoader><StoreAccountManage /></PageLoader></PrivateRoute>} />
      <Route path="/stores/consumers" element={<PrivateRoute><PageLoader><ConsumerManage /></PageLoader></PrivateRoute>} />
      <Route path="/stores/registration-audit" element={<PrivateRoute><PageLoader><CustomerRegistrationAudit /></PageLoader></PrivateRoute>} />
      <Route path="/stores/:id" element={<PrivateRoute><PageLoader><StoreDetail /></PageLoader></PrivateRoute>} />
      {/* 采购管理 */}
      <Route path="/purchase/apply" element={<PrivateRoute><PageLoader><PurchaseApply /></PageLoader></PrivateRoute>} />
      <Route path="/purchase/inventory" element={<PrivateRoute><PageLoader><FabricInventory /></PageLoader></PrivateRoute>} />
      <Route path="/purchase/fabric-import" element={<PrivateRoute><PageLoader><FabricImport /></PageLoader></PrivateRoute>} />
      <Route path="/purchase/sample-books" element={<PrivateRoute><PageLoader><SampleBookConfig /></PageLoader></PrivateRoute>} />
      <Route path="/purchase/order-inventory" element={<PrivateRoute><PageLoader><OrderInboundOutbound /></PageLoader></PrivateRoute>} />
      {/* 仓储物流 */}
      <Route path="/warehouse/receive" element={<PrivateRoute><PageLoader><ReceiveManage /></PageLoader></PrivateRoute>} />
      <Route path="/warehouse/qc" element={<PrivateRoute><PageLoader><QCManage /></PageLoader></PrivateRoute>} />
      <Route path="/warehouse/ship" element={<PrivateRoute><PageLoader><ShipManage /></PageLoader></PrivateRoute>} />
      {/* 成衣商城 */}
      <Route path="/ready-wear" element={<PrivateRoute><PageLoader><ReadyWearManage /></PageLoader></PrivateRoute>} />
      <Route path="/ready-wear/create" element={<PrivateRoute><PageLoader><ReadyWearCreate /></PageLoader></PrivateRoute>} />
      {/* 基础数据 */}
      <Route path="/master/price-system" element={<PrivateRoute><PageLoader><PriceSystemConfig /></PageLoader></PrivateRoute>} />
      <Route path="/master/front-category" element={<PrivateRoute><PageLoader><FrontCategory /></PageLoader></PrivateRoute>} />
      <Route path="/master/commission-rules" element={<PrivateRoute><PageLoader><CommissionConfig /></PageLoader></PrivateRoute>} />
      <Route path="/master/penalty-rules" element={<PrivateRoute><PageLoader><PenaltyConfig /></PageLoader></PrivateRoute>} />
      <Route path="/master/base-order-config" element={<PrivateRoute><PageLoader><BaseOrderConfig /></PageLoader></PrivateRoute>} />
      <Route path="/master/ease-rule" element={<PrivateRoute><PageLoader><EaseRuleConfig /></PageLoader></PrivateRoute>} />
      <Route path="/master/option-rules" element={<PrivateRoute><PageLoader><DesignOptionRules /></PageLoader></PrivateRoute>} />

      {/* 供应商管理 */}
      <Route path="/supplier/material" element={<PrivateRoute><PageLoader><MaterialSupplier /></PageLoader></PrivateRoute>} />
      <Route path="/supplier/factory" element={<PrivateRoute><PageLoader><FactorySupplier /></PageLoader></PrivateRoute>} />
      <Route path="/supplier/factory-mapping" element={<PrivateRoute><PageLoader><FactoryMappingManager /></PageLoader></PrivateRoute>} />
      <Route path="/supplier/factory-mapping/import" element={<PrivateRoute><PageLoader><FactoryMappingImport /></PageLoader></PrivateRoute>} />
      {/* 财务管理 */}
      <Route path="/finance/my-salary" element={<PrivateRoute><PageLoader><MySalary /></PageLoader></PrivateRoute>} />
      <Route path="/finance/salary-config" element={<PrivateRoute><PageLoader><SalaryConfig /></PageLoader></PrivateRoute>} />
      <Route path="/finance/commission-calculator" element={<PrivateRoute><PageLoader><CommissionCalculator /></PageLoader></PrivateRoute>} />
      <Route path="/finance/salary-calculator" element={<PrivateRoute><PageLoader><SalaryCalculator /></PageLoader></PrivateRoute>} />
      <Route path="/finance/salary" element={<PrivateRoute><PageLoader><SalarySummary /></PageLoader></PrivateRoute>} />
      <Route path="/finance/year-end-bonus" element={<PrivateRoute><PageLoader><YearEndBonus /></PageLoader></PrivateRoute>} />
      <Route path="/finance/discount-approval" element={<PrivateRoute><PageLoader><DiscountApproval /></PageLoader></PrivateRoute>} />
      <Route path="/finance/quote-generator" element={<PrivateRoute><PageLoader><QuoteGenerator /></PageLoader></PrivateRoute>} />
      <Route path="/finance/quote" element={<PrivateRoute><PageLoader><QuoteTool /></PageLoader></PrivateRoute>} />
      <Route path="/finance/sales-prices" element={<PrivateRoute><PageLoader><SalesPriceTable /></PageLoader></PrivateRoute>} />
      <Route path="/finance/invoices" element={<PrivateRoute><PageLoader><InvoiceManage /></PageLoader></PrivateRoute>} />
      <Route path="/finance/factory-reconciliations" element={<PrivateRoute><PageLoader><FactoryReconciliationList /></PageLoader></PrivateRoute>} />
      <Route path="/finance/invoices/:id" element={<PrivateRoute><PageLoader><InvoiceDetail /></PageLoader></PrivateRoute>} />
      <Route path="/finance/invoice-generator" element={<PrivateRoute><PageLoader><InvoiceGenerator /></PageLoader></PrivateRoute>} />
      <Route path="/finance/invoice-tool" element={<PrivateRoute><PageLoader><InvoiceTool /></PageLoader></PrivateRoute>} />
      {/* 公开分享页面 — 无需登录 */}
      <Route path="/quote-view" element={<PageLoader><QuoteView /></PageLoader>} />
      <Route path="/supplier/confirm" element={<PageLoader><SupplierConfirm /></PageLoader>} />
      <Route path="/purchase/batch-share" element={<PageLoader><BatchShareView /></PageLoader>} />
      {/* 系统设置 */}
      <Route path="/settings/users" element={<PrivateRoute><PageLoader><UserManage /></PageLoader></PrivateRoute>} />
      <Route path="/settings/roles" element={<PrivateRoute><PageLoader><RoleManage /></PageLoader></PrivateRoute>} />
      <Route path="/settings/switch-user" element={<PrivateRoute><PageLoader><SwitchUser /></PageLoader></PrivateRoute>} />
      <Route path="/settings/system-config" element={<PrivateRoute><PageLoader><SystemConfig /></PageLoader></PrivateRoute>} />
      <Route path="/settings/messages" element={<PrivateRoute><PageLoader><MessageCenter /></PageLoader></PrivateRoute>} />
      <Route path="/settings/operation-logs" element={<PrivateRoute><PageLoader><OperationLog /></PageLoader></PrivateRoute>} />
      <Route path="/settings/recycle-bin" element={<PrivateRoute><PageLoader><RecycleBin /></PageLoader></PrivateRoute>} />
      {/* 报表中心 */}
      <Route path="/reports/sales" element={<PrivateRoute><PageLoader><SalesReport /></PageLoader></PrivateRoute>} />
      <Route path="/reports/orders" element={<PrivateRoute><PageLoader><OrderReport /></PageLoader></PrivateRoute>} />
      <Route path="/reports/costs" element={<PrivateRoute><PageLoader><CostReport /></PageLoader></PrivateRoute>} />
      <Route path="/reports/profit" element={<PrivateRoute><PageLoader><ProfitReport /></PageLoader></PrivateRoute>} />
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AppInner() {
  const { locale } = useLocale();
  const { isDark } = useTheme();
  return (
    <ConfigProvider
      locale={locale === 'zh' ? zhCN : enUS}
      theme={{
        token: {
          colorPrimary: '#1677ff',
          borderRadius: 8,
          fontSize: 14,
          colorBgContainer: '#fff',
          colorText: '#1f1f1f',
          colorTextSecondary: '#595959',
          colorBorder: '#f0f0f0',
        },
        algorithm: isDark ? theme.darkAlgorithm : undefined,
      }}
    >
      <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </ConfigProvider>
  );
}

function App() {
  return (
    <LocaleProvider>
      <ThemeProvider>
        <AppInner />
      </ThemeProvider>
    </LocaleProvider>
  );
}

export default App;
